/**
 * Loading and caching of Noto Animated Emoji Lottie animations.
 *
 * The manifest (src/generated/emojiManifest.ts) is the single source of truth for which
 * emoji have an animation. Anything not listed there is rendered as plain text.
 */

import { DotLottie } from '@lottiefiles/dotlottie-web'
import wasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url'

import { EMOJI_CDN_BASE_URL, EMOJI_FETCH_TIMEOUT_MS } from '../config'
import emojiManifest from '../generated/emojiManifest.json'

const ANIMATED_EMOJI: Readonly<Record<string, string>> = emojiManifest.animated

// Serve the player runtime from our own origin instead of the library's default CDN.
DotLottie.setWasmUrl(wasmUrl)

/** Playback modes an emoji can be rendered with. */
export type EmojiPlayback = 'once' | 'loop'

/**
 * Raised when the CDN has no usable animation for a codepoint.
 *
 * Diagnostic only: callers degrade to the plain character, this never reaches the UI.
 */
export class EmojiAnimationUnavailableError extends Error {
  constructor(
    readonly codepoint: string,
    readonly status: number
  ) {
    super(`emoji-animation-unavailable codepoint=${codepoint} status=${status}`)
    this.name = 'EmojiAnimationUnavailableError'
  }
}

/**
 * Codepoints that accompany ongoing states, where a frozen last frame looks broken.
 * Game modes can override this per occurrence via the data attribute (see useAnimatedEmoji).
 */
export const LOOPED_CODEPOINTS: ReadonlySet<string> = new Set([
  '1f3c6', // 🏆
  '1f389', // 🎉
  '2728', // ✨
  '23f3', // ⏳
])

const graphemeSegmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })

const animationCache = new Map<string, string>()
const inFlight = new Map<string, Promise<string>>()
/**
 * Codepoints the CDN answered 404 for. Only deterministic absence is remembered: a timeout
 * or a network hiccup must not disable an emoji for the rest of the session.
 */
const missingCodepoints = new Set<string>()

/**
 * Splits text into grapheme clusters.
 */
export function segmentGraphemes(text: string): string[] {
  return Array.from(graphemeSegmenter.segment(text), (entry) => entry.segment)
}

/**
 * Resolves a grapheme to its animated Noto codepoint.
 *
 * @returns the codepoint, or null when the grapheme has no animation.
 */
export function resolveAnimatedCodepoint(grapheme: string): string | null {
  if (!Object.prototype.hasOwnProperty.call(ANIMATED_EMOJI, grapheme)) {
    return null
  }
  return ANIMATED_EMOJI[grapheme]
}

/**
 * Decides the playback mode for a codepoint when no explicit override is given.
 */
export function defaultPlayback(codepoint: string): EmojiPlayback {
  return LOOPED_CODEPOINTS.has(codepoint) ? 'loop' : 'once'
}

/**
 * Fetches the Lottie animation for a codepoint, deduplicating concurrent requests.
 *
 * Rejects on network failure, non-OK status or timeout. Callers degrade to the plain
 * character; a hung connection never resolves on its own, hence the explicit timeout.
 */
export function loadEmojiAnimation(codepoint: string): Promise<string> {
  const cached = animationCache.get(codepoint)
  if (cached !== undefined) {
    return Promise.resolve(cached)
  }

  if (missingCodepoints.has(codepoint)) {
    return Promise.reject(new EmojiAnimationUnavailableError(codepoint, 404))
  }

  const pending = inFlight.get(codepoint)
  if (pending !== undefined) {
    return pending
  }

  const request = fetchAnimation(codepoint)
    .then((json) => {
      animationCache.set(codepoint, json)
      return json
    })
    .catch((error: unknown) => {
      if (error instanceof EmojiAnimationUnavailableError && error.status === 404) {
        missingCodepoints.add(codepoint)
      }
      throw error
    })
    .finally(() => {
      inFlight.delete(codepoint)
    })

  inFlight.set(codepoint, request)
  return request
}

async function fetchAnimation(codepoint: string): Promise<string> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), EMOJI_FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(`${EMOJI_CDN_BASE_URL}/${codepoint}/lottie.json`, {
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new EmojiAnimationUnavailableError(codepoint, response.status)
    }
    return await response.text()
  } finally {
    clearTimeout(timer)
  }
}
